/**
 * ChronologyTimeline - typed renderer for the `chronology` payload.
 *
 * A vertical dated rail: date · event · significance · citations.
 *
 * Payload order is authoritative. The engine fixes the sequence deliberately
 * (undated and approximate events are placed where they belong in the
 * narrative), so this NEVER re-sorts.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChronologyEvent, ChronologyPayload, ResearchCitation } from '../types';
import { CitationChips, CitationHandlers, citationNumbers } from './CitationChip';
import InlineCitedText from './InlineCitedText';

interface Props extends CitationHandlers {
  payload: ChronologyPayload;
  citations: ResearchCitation[];
}

/** What happened, under any of the spellings the engine has used. */
const eventText = (event: ChronologyEvent): string =>
  event.event || event.title || event.description || '';

const ChronologyTimeline: React.FC<Props> = ({ payload, citations, ...handlers }) => {
  const { t } = useTranslation();
  // Rendered in payload order - see the note above.
  const events = Array.isArray(payload.events) ? payload.events : [];
  const gaps = Array.isArray(payload.gaps) ? payload.gaps : [];

  return (
    <div className="font-sans space-y-3 animate-fade-in" data-testid="chronology-timeline">
      {payload.title && (
        <h3 className="text-sm font-semibold text-fg-strong">{payload.title}</h3>
      )}
      {payload.summary && (
        <p className="text-sm leading-relaxed text-fg-body">
          <InlineCitedText text={payload.summary} citations={citations} {...handlers} />
        </p>
      )}

      <ol className="relative ps-4 border-s border-hairline">
        {events.map((event, i) => {
          const text = eventText(event);
          const approximate = !!event.date_precision && event.date_precision !== 'day';
          return (
            <li
              key={i}
              className="relative pb-4 last:pb-0 reveal-item"
              style={{ '--reveal-delay': `${Math.min(i, 10) * 50}ms` } as React.CSSProperties}
              data-testid="chronology-event"
            >
              <span
                className="absolute -start-[21px] top-1.5 w-2 h-2 rounded-full bg-champagne ring-2 ring-bg"
                aria-hidden
              />
              <div className="flex items-baseline gap-2 flex-wrap">
                <time className="text-[11px] font-semibold tracking-wide text-fg-muted">
                  {event.date || t('Undated')}
                </time>
                {approximate && (
                  <span className="text-[10px] text-fg-muted/80">
                    ({t('approximate')})
                  </span>
                )}
                {event.actors && event.actors.length > 0 && (
                  <span className="text-[11px] text-fg-muted">{event.actors.join(', ')}</span>
                )}
              </div>
              {text && (
                <p className="mt-0.5 text-sm leading-relaxed text-fg-body">
                  <InlineCitedText text={text} citations={citations} {...handlers} />
                  <CitationChips
                    numbers={citationNumbers(event)}
                    citations={citations}
                    {...handlers}
                  />
                </p>
              )}
              {event.significance && (
                <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
                  {event.significance}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {gaps.length > 0 && (
        <div className="pt-1 border-t border-hairline-soft">
          <p className="text-[11px] font-medium text-fg-muted mb-1">{t('Gaps in the record')}</p>
          <ul className="space-y-1">
            {gaps.map((gap, i) => (
              <li key={i} className="text-xs text-fg-muted leading-relaxed">
                {gap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChronologyTimeline;
